import { Paper, Stack, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import AppStore from 'assets/images/AppStore.png';
import GooglePlay from 'assets/images/GooglePlay.png';
import DOMPurify from 'dompurify';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { useSearchParams } from 'react-router';
import QRCodeValueUtil from 'utils/QRCodeValue/QRCodeValue.util';
import Loader from 'v2/components/Loader/Loader';
import QRCode from 'v2/components/QRCode/QRCode';

type LandingType = 'ACCESS' | 'CONFIG';

export interface Props {
  pageVariant: LandingType;
}

const GooglePlayButton = styled('div')({
  backgroundImage: `url("${GooglePlay}")`,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  width: 120,
  height: 36
});

const AppStoreButton = styled('div')({
  backgroundImage: `url("${AppStore}")`,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  width: 120,
  height: 40
});

const LandingPage: React.FC<React.PropsWithChildren<Props>> = ({ pageVariant }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  return (
    <Stack style={isMobile ? { overflow: 'auto', height: '100vh' } : {}}>
      <Stack
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
          ...(isMobile ? { margin: '2rem 0' } : {})
        }}
      >
        {!isMobile && (
          <Typography variant="h3" color="primary">
            ATLAS
          </Typography>
        )}
        <Paper
          sx={{
            backgroundColor: theme.palette.background.default,
            display: 'flex',
            borderRadius: 1,
            ...(isMobile
              ? {
                  flexDirection: 'column',
                  p: '1.5rem',
                  gap: '1.5rem',
                  mt: '2rem'
                }
              : {
                  flexDirection: 'row',
                  p: '3rem 5rem',
                  gap: '4.375rem'
                })
          }}
        >
          <Stack flexDirection="column" gap="1.5rem">
            <Typography variant={isMobile ? 'h5' : 'h4'} color="textPrimary">
              {`ATLAS ${pageVariant === 'ACCESS' ? `${translation.access}` : `${translation.config}`} ${
                translation.application
              }`}
            </Typography>
            <Stack
              gap={1}
              style={{
                display: 'flex',
                flexDirection: 'column',
                ...(isMobile ? { width: 'calc(90vw - 3rem);' } : { width: '422px' })
              }}
            >
              {isMobile ? (
                <>
                  {pageVariant === 'ACCESS' ? (
                    <Typography variant="body2" color="textPrimary">
                      {translation.link_profile_to_atlas_access_app}
                    </Typography>
                  ) : (
                    <Typography variant="body1" color="textPrimary">
                      {translation.installer_invite_download_config}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textPrimary">
                    {pageVariant === 'ACCESS' ? translation.download_access_app : translation.download_config_app}.
                  </Typography>
                </>
              ) : (
                <>
                  {pageVariant === 'ACCESS' ? (
                    <Typography variant="body1" color="textPrimary">
                      {translation.link_profile_to_atlas_access_app}
                    </Typography>
                  ) : (
                    <Typography variant="body1" color="textPrimary">
                      {translation.installer_invite_scan_qr}
                    </Typography>
                  )}
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        `1. ${
                          pageVariant === 'ACCESS' ? translation.download_access_app : translation.download_config_app
                        }.`
                      )
                    }}
                  />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(`2. ${translation.onboarding_open_qr_scanner}.`)
                    }}
                  />
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(`3. ${translation.register_device_scan_qr}`)
                    }}
                  />
                </>
              )}
            </Stack>
          </Stack>
          <Stack flexDirection="column" gap="5rem">
            {code ? (
              !isMobile && (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  width="14rem"
                  height="14rem"
                  id="registration-page-qr-code"
                >
                  <QRCode value={QRCodeValueUtil.getQrCodeValue(code)} />
                </Stack>
              )
            ) : (
              <Stack flexDirection="column" alignItems="center" justifyContent="center" width="14rem" height="14rem">
                <Loader />
                <Typography variant="h6" color="textPrimary">
                  {translation.generating_qr}
                </Typography>
              </Stack>
            )}
            <Stack
              gap={1}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              flexDirection={isMobile ? 'row' : 'column'}
            >
              {pageVariant === 'ACCESS' && (
                <a
                  href="https://play.google.com/store/apps/details?id=com.tkhsecurity.siqura\u0026hl=en_US/"
                  target="_blank"
                  rel="noreferrer"
                  id="google-play-button"
                >
                  <GooglePlayButton />
                </a>
              )}
              <a
                href={
                  pageVariant === 'ACCESS'
                    ? 'https://apps.apple.com/eng/app/atlas-ACCESS/id1625855308'
                    : 'https://apps.apple.com/nl/app/atlas-CONFIG/id1634787760?l'
                }
                target="_blank"
                rel="noreferrer"
                id="app-store-button"
              >
                <AppStoreButton />
              </a>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};

export default LandingPage;
